import * as React from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  Building2,
  CalendarClock,
  Copy,
  FileText,
  LayoutDashboard,
  Phone,
  PhoneCall,
  Plus,
  Settings as SettingsIcon,
  Upload,
  Users,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { formatPhone } from '@/lib/utils';
import { Dialog, DialogContent } from './ui/overlays';

/**
 * Command palette. Doubles as global search: typing digits searches phone
 * numbers, anything else runs full-text search over leads and notes.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { user, authEnabled } = useAuth();
  const isVa = authEnabled && user?.role === 'va';
  const [query, setQuery] = React.useState('');
  const [debounced, setDebounced] = React.useState('');

  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 150);
    return () => clearTimeout(timer);
  }, [query]);

  React.useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const search = useQuery({
    queryKey: ['global-search', debounced],
    queryFn: () => api.search(debounced),
    enabled: open && debounced.length >= 2,
    staleTime: 10_000,
  });

  const go = React.useCallback(
    (path: string) => {
      onOpenChange(false);
      navigate(path);
    },
    [navigate, onOpenChange],
  );

  const results = search.data;
  const hasResults =
    !!results && (results.leads.length > 0 || results.notes.length > 0 || results.phoneMatches.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" className="top-[15%] translate-y-0 p-0">
        <Command shouldFilter={false} loop className="overflow-hidden">
          <div className="border-b border-border px-3">
            <Command.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder="Search leads, notes, phone numbers, or jump to a screen…"
              className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          <Command.List className="max-h-[24rem] overflow-y-auto p-1.5">
            {debounced.length >= 2 && search.isFetching && !hasResults && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">Searching…</div>
            )}

            {debounced.length >= 2 && !search.isFetching && !hasResults && (
              <Command.Empty className="px-3 py-6 text-center text-xs text-muted-foreground">
                Nothing matched “{debounced}”.
              </Command.Empty>
            )}

            {results && results.phoneMatches.length > 0 && (
              <Command.Group heading="Phone matches" className="palette-group">
                {results.phoneMatches.map((lead) => (
                  <Command.Item
                    key={`phone-${lead.id}`}
                    value={`phone-${lead.id}`}
                    onSelect={() => go(`/leads/${lead.id}`)}
                    className="palette-item"
                  >
                    <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate">{lead.businessName}</span>
                    <span className="font-mono text-2xs tabular-nums text-muted-foreground">
                      {formatPhone(lead.primaryPhone)}
                    </span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {results && results.leads.length > 0 && (
              <Command.Group heading="Leads" className="palette-group">
                {results.leads.map((lead) => (
                  <Command.Item
                    key={`lead-${lead.id}`}
                    value={`lead-${lead.id}`}
                    onSelect={() => go(`/leads/${lead.id}`)}
                    className="palette-item"
                  >
                    <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate">{lead.businessName}</span>
                    {lead.city && <span className="text-2xs text-muted-foreground">{lead.city}</span>}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {results && results.notes.length > 0 && (
              <Command.Group heading="Notes" className="palette-group">
                {results.notes.map((note) => (
                  <Command.Item
                    key={`note-${note.id}`}
                    value={`note-${note.id}`}
                    onSelect={() => go(`/leads/${note.leadId}`)}
                    className="palette-item"
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate">{note.leadBusinessName}</p>
                      <p className="truncate text-2xs text-muted-foreground">{note.snippet}</p>
                    </div>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {debounced.length < 2 && (
              <>
                <Command.Group heading="Actions" className="palette-group">
                  {!isVa && (
                    <Command.Item value="new-lead" onSelect={() => go('/leads?new=1')} className="palette-item">
                      <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      New lead
                    </Command.Item>
                  )}
                  <Command.Item value="new-queue" onSelect={() => go('/queue?new=1')} className="palette-item">
                    <PhoneCall className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    Build a call queue
                  </Command.Item>
                  {!isVa && (
                    <Command.Item value="import" onSelect={() => go('/imports?new=1')} className="palette-item">
                      <Upload className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      Import a file
                    </Command.Item>
                  )}
                </Command.Group>

                <Command.Group heading="Go to" className="palette-group">
                  {[
                    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
                    { path: '/queue', label: 'Call Queue', icon: PhoneCall },
                    { path: '/leads', label: 'Leads', icon: Building2 },
                    { path: '/follow-ups', label: 'Follow-Ups', icon: CalendarClock },
                    // Admin-only destinations. Search itself stays available to
                    // everyone and is scoped on the server.
                    ...(isVa
                      ? []
                      : [
                          { path: '/imports', label: 'Imports', icon: Upload },
                          { path: '/duplicates', label: 'Duplicates', icon: Copy },
                          { path: '/customers', label: 'Customers', icon: Users },
                          { path: '/reports', label: 'Reports', icon: BarChart3 },
                          { path: '/settings', label: 'Settings', icon: SettingsIcon },
                        ]),
                  ].map(({ path, label, icon: Icon }) => (
                    <Command.Item
                      key={path}
                      value={`goto-${label}`}
                      onSelect={() => go(path)}
                      className="palette-item"
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      {label}
                    </Command.Item>
                  ))}
                </Command.Group>
              </>
            )}
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
